<template>
    <v-dialog v-model="showInternal" content-class="dc-above-atlas-flyout" style="background-color: white" @click:outside="$emit('close');">
  <v-card>
    <v-card-title>
      {{ title }}
      <v-spacer></v-spacer>
      <v-switch
        v-model="highlightChanges"
        label="Highlight Differences"
        class="mr-4"
      ></v-switch>
      <v-btn
        icon
        large
        class="btn-background"
        @click="showInternal = false; $emit('close');"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
  <div>
    <v-row v-if="isLoading" class="d-flex justify-center">
      <v-progress-circular indeterminate color="primary" size="50">
      </v-progress-circular>
    </v-row>
    <div v-else class="sync-grid-container">
      <div class="grid-headers-wrapper" ref="headersWrapper">
        <div class="grid-headers">
          <template v-for="(d, di) in documents">
            <div
              v-if="di === 1"
              :key="'resize_' + d.doc_id"
              class="resize-handle"
              @mousedown="startResize($event)"
            >
              <div class="resize-handle-inner"></div>
            </div>
            <div
              v-else-if="di > 1"
              :key="'resize_' + d.doc_id"
              class="resize-filler"
            >
            </div>
            <div :key="'ddra' + d.doc_id" :style="d.docContainerStyle"
                @mouseover="d.hover = !fixPrimaryDoc || di > 0"
                @mouseout="d.hover = false">
              <div class="grid-move-handle" :style="!d.hover ? 'visibility: hidden;' : null">
                <v-icon class="ml-2" @click="reOrder(di, di - 1)" :disabled="di === 0" title="Move document to the left">mdi-chevron-left</v-icon>
                <v-icon class="ml-2" @click="removeDocument(di)" small title="Remove document from the list">mdi-sticker-remove-outline</v-icon>
                <v-icon class="mx-2 drag-handle" title="Drag to reorder"
                  draggable="true"
                  @dragstart="handleDragStart($event, di)"
                  @dragend="handleDragEnd"
                  @dragover.prevent="handleDragOver($event, di)"
                  @dragleave="handleDragLeave"
                  @drop.prevent="handleDrop(di)">mdi-drag</v-icon>
                <v-icon class="mr-2" @click="reOrder(di, di + 1)" :disabled="di + 1 === documents.length" title="Move document to the right">mdi-chevron-right</v-icon>
              </div>
              <div
                :key="'d' + d.doc_id"
                class=""
                :class="{
                  'header-cell': di > 0 || !fixPrimaryDoc,
                  'header-cell-fixed': di === 0 && fixPrimaryDoc,
                  dragging: draggedIndex === di,
                  'drag-over': dragOverIndex === di,
                }"
                :style="d.docContainerStyle" style="display: flex;"
                :draggable="!fixPrimaryDoc || di > 0"
                @dragstart="handleDragStart($event, di)"
                @dragend="handleDragEnd"
                @dragover.prevent="handleDragOver($event, di)"
                @dragleave="handleDragLeave"
                @drop.prevent="handleDrop(di)"
              >
                <div class="header-doc"
                  :style="d.docHeaderStyle">
                  <v-row dense v-if="!d.file_id">
                    <v-col>
                      {{ d.doc_name }}
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end">
                        <v-icon class="mr-2" style="font-size: 20px; color: #7d93b4 !important"
                          v-if="showAllComments && showWorkflowActions && !d.comments_total"
                          @click="showComments(d)" title="Show Comment Panel"
                          >mdi-message-outline</v-icon
                      >
                      <v-badge :color="d.comments_unresolved ? 'green' : 'blue'" overlap dot
                        v-else-if="showAllComments && showWorkflowActions && d.comments_total"
                        :title="d.comments_total + ' comment' + (d.comments_total > 1 ? 's' : '') + ' (' + d.comments_unresolved + ' unresolved)'">
                        <v-icon class="mr-2" style="font-size: 20px; color: #7d93b4 !important"
                          @click="showComments(d)" title="Show Comment Panel"
                          >mdi-message-outline</v-icon
                        >
                      </v-badge>
                      <v-icon class="ml-2"
                        v-if="showWorkflowActions"
                        @click="openDocActionsMenuContext($event, d)"
                        >mdi-dots-vertical</v-icon
                      >
                    </v-col>
                    <v-col cols="12">
                      {{ d.reference }}
                      <v-chip
                        small
                        class="status-chip"
                        label
                        :color="d.status_colour"
                        >{{ d.status }}</v-chip
                      >
                      <span
                        v-if="di === 0 && fixPrimaryDoc"
                        class="ml-2 float-right current-doc-label"
                        small
                      >
                        CURRENT {{ d.tmpl_name.toUpperCase() }}
                      </span>
                      <v-chip v-if="d.matchPctContent" class="ml-2" small>
                        {{ d.matchPctContent + "%" }} match
                      </v-chip>
                      <v-chip v-else-if="d.matchPctName" class="ml-2" small>
                        {{ d.matchPctContent + "%" }} match
                      </v-chip>
                    </v-col>
                  </v-row>
                  <v-row dense v-else>
                    <v-col>
                      {{ d.doc_name || d.file_name }}
                    </v-col>
                    <v-col cols="1" class="d-flex justify-end">
                      <v-btn
                        small
                        icon
                        v-if="d.file_id"
                        title="Download File"
                        @click="openItem(d)"
                        :style="d.reference || d.file_id ? 'cursor: pointer' : ''"
                        ><v-icon>download</v-icon></v-btn
                      >
                    </v-col>
                    <v-col cols="12">
                      Created {{ d.created_date }} by
                      {{ d.created_by_user || d.created_by }}
                      <v-chip small class="status-chip" label color="blue"
                        >Upload</v-chip
                      >
                    </v-col>
                  </v-row>
                </div>
                <div class="header-comment"
                  v-if="d.commentStyle"
                  :key="'d_c_' + di"
                  :style="d.commentStyle"
                >
                  <v-icon class="mr-2"
                    @click="showComments(d)" title="Hide Comment Panel"
                    >mdi-chevron-left</v-icon
                  >
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="grid-wrappers" ref="gridsWrapper">
        <template v-for="(d, di) in documents">
          <div
            v-if="di === 1"
            :key="'resize_content_' + d.doc_id"
            class="resize-handle"
            @mousedown="startResize($event)"
          >
            <div class="resize-handle-inner"></div>
          </div>
          <div
            v-else-if="di > 1"
            :key="'resize_content_' + d.doc_id"
            class="resize-filler"
          >
          </div>
          <div v-if="!d.file_id" :key="'dd' + d.doc_id" :class="{
                  'doc-detail-container': di > 0 || !fixPrimaryDoc,
                  'doc-detail-container-fixed': di === 0 && fixPrimaryDoc,
                }"
              :style="d.docContainerStyle">
            <div class="doc-part-container" :ref="`scrollContainer${di}`" :style="d.docPartStyle">
              <template
                v-for="(tp, tpi) in d.templateParts"
              >
                <div   
                  v-for="dpi in tp.def?.partsCount"
                  no-gutters class="part-content"
                  :key="`dd${d.doc_id}tp${tpi}_${dpi}`"
                  :class="[dpi % 2 == 1 ? 'odd' : 'even', tp.def.partClass]"
                  :ref="`set_${d.compareSet}_doc_${di}_row_${tpi}_${dpi}`"
                  :id="tp.data[dpi - 1] ?`container_${tp.data[dpi - 1].dp_id}` : null"
                >
                  <v-container
                    v-if="
                      tp.def &&
                      tp.data &&
                      tp.data.length >= dpi &&
                      tp.data[dpi - 1]
                    "
                    :class="[
              d.highlightedDocPartForCommentHover?.dp_id === tp.data[dpi - 1].dp_id
                ? 'highlightedCommentPartHover'
                : '',
              d.highlightedDocPartForCommentFocus?.dp_id === tp.data[dpi - 1].dp_id
                ? 'highlightedCommentPartFocus'
                : '',]"
                  >
                    <DocumentVersionCompareTagSummary
                      v-if="tp.data[dpi - 1].tag_value_id"
                      :docPartTag="tp.data[dpi - 1]"
                      :highlightChanges="highlightChanges"
                    >
                    </DocumentVersionCompareTagSummary>

                    <DocumentVersionComparePart
                      v-else
                      :dp="tp.data[dpi - 1]"
                      hideCompareVersion
                      :tp="tp.def"
                      :highlightChanges="highlightChanges"
                      :commentActions="d.commentActions"
                      :partCommentUpdate="partCommentUpdate"
                      @showComments="(dp) => showComments(d, dp)"
                    >
                    </DocumentVersionComparePart>
                  </v-container>
                  <!-- </div> -->
                </div>
              </template>
            </div>
            <div
              v-if="d.commentStyle"
              :key="'dd_c_' + d.doc_id" class="doc-comment-container"
              :style="d.commentStyle"
            >
              <DocumentComments class="mt-4" :document="d" all hideHeader
                :target_doc_part="d.comment_doc_part"
                heightAdjustment="370px"
                @commentclick="(comment) => scrollToSectionForComment(comment, d)"
                @saved="(data) => commentSaved(data, d)"
                @commentResolved="(data) => commentResolved(data, d)"
                @mouseenter="(part) => highlightSectionForCommentHover(d, part)"
                @mouseleave="highlightSectionForCommentHover(d)"
                @resetCommentPart="resetSectionForComment(d)"
              ></DocumentComments>
            </div>
          </div>
          <div v-else :key="'dd' + d.doc_id" class="doc-detail-container"
              :style="d.docPartStyle">            
            <div v-if="d.contentLoading" class="d-flex justify-center">
              <v-progress-circular indeterminate size="50" color="primary">
              </v-progress-circular>
            </div>
            <div v-else class="doc-part-container pa-4">
              <span v-html="d.contentHtml"></span>
            </div>
          </div>
        </template>
      </div>
    </div>
    <!-- 
    <v-row class="doc-container" ref="docContainer">
      <v-col
        v-for="(d, di) in documents"
        :key="'d' + di"
        :style="{ 'min-height': containerHeight, 'flex-grow': d.length || 1 }"
      >
        <v-row class="doc-header">
          <template v-if="d.doc_id && !d.file_id">
            <v-col
              :key="`vh${di}`"
              :class="{ sticky: multiDoc && documents.length === 1 && di === 0 }"
            >
              <v-row dense>
                <v-col>
                  {{ d.doc_name }}
                </v-col>
                <v-col cols="1" class="d-flex justify-end">
                  <v-icon
                    v-if="showWorkflowActions"
                    @click="openDocActionsMenuContext($event, d)"
                    >mdi-dots-vertical</v-icon
                  >
                </v-col>
                <v-col cols="12">
                  {{ d.reference }}
                  <v-chip
                    small
                    class="status-chip"
                    label
                    :color="d.status_colour"
                    >{{ d.status }}</v-chip
                  >
                  <v-chip
                    v-if="
                      d.doc_id === currentDocId &&
                      d.tmpl_name?.toLowerCase().includes('job')
                    "
                    class="ml-2"
                    small
                  >
                    CURRENT JOB
                  </v-chip>
                  <v-chip v-if="d.matchPctContent" class="ml-2" small>
                    {{ d.matchPctContent + "%" }} match
                  </v-chip>
                  <v-chip v-else-if="d.matchPctName" class="ml-2" small>
                    {{ d.matchPctContent + "%" }} match
                  </v-chip>
                </v-col>
              </v-row>
            </v-col>
            <v-col :key="`vh_c${di}`">com</v-col>
          </template>
          <template v-else-if="d.file_id">
            <v-col>
              <v-row dense>
                <v-col>
                  {{ d.doc_name || d.file_name }}
                </v-col>
                <v-col cols="1" class="d-flex justify-end">
                  <v-btn
                    small
                    icon
                    v-if="d.file_id"
                    title="Download File"
                    @click="openItem(d)"
                    :style="d.reference || d.file_id ? 'cursor: pointer' : ''"
                    ><v-icon>download</v-icon></v-btn
                  >
                </v-col>
                <v-col cols="12">
                  Created {{ d.created_date }} by
                  {{ d.created_by_user || d.created_by }}
                  <v-chip small class="status-chip" label color="blue"
                    >Upload</v-chip
                  >
                </v-col>
              </v-row>
            </v-col>
          </template>
        </v-row>

        <template v-if="d.doc_id && !d.file_id">
          <v-row>
            <v-col>
              <template v-for="(tp, tpi) in d.templateParts">
                <v-row
                  v-if="
                    tp.def.attrs.some(
                      (a) => a.tag_type_id && a.tag_viewer === 'grid'
                    )
                  "
                  class="HeaderPart tag"
                  :key="'d' + di + 'tpi' + tpi"
                >
                  <v-col
                    :class="{
                      sticky: multiDoc && documents.length === 1 && di === 0,
                    }"
                  >
                    {{
                      utils.pluralise(
                        tp.def.attrs.find(
                          (a) => a.tag_type_id && a.tag_viewer === "grid"
                        ).tag_type_name
                      )
                    }}
                  </v-col>
                </v-row>

                <v-row
                  v-for="dpi in tp.def.partsCount"
                  no-gutters
                  :key="`tp${tp.def.tp_id}_${dpi}`"
                  :class="[dpi % 2 == 1 ? 'odd' : 'even']"
                >
                  <v-col
                    :class="{
                      first: tpi === 0 && dpi === 1,
                      sticky: multiDoc && documents.length === 1 && di === 0,
                    }"
                  >
                    <template v-if="tp.data && tp.data.length >= dpi">
                      <DocumentVersionCompareTagSummary
                        v-if="tp.data[dpi - 1].tag_value_id"
                        :docPartTag="tp.data[dpi - 1]"
                        :highlightChanges="highlightChanges"
                      >
                      </DocumentVersionCompareTagSummary>

                      <DocumentVersionComparePart
                        v-else
                        :dp="tp.data[dpi - 1]"
                        :tp="tp.def"
                        :highlightChanges="highlightChanges"
                        :commentActions="d.commentActions"
                      >
                      </DocumentVersionComparePart>
                    </template>
                  </v-col>
                </v-row>
              </template>
            </v-col>
            <v-col :key="'d_c' + di">
              <div style="width: 100%; height: 100%; max-height: 100%">
                <DocumentComments class="mt-4" :document="d" all hideHeader>
                </DocumentComments>
                :target_doc_part="comment_doc_part"
                  @mouseenter="highlightSectionForCommentHover"
                  @mouseleave="highlightSectionForCommentHover(null)"
                  @commentclick="scrollToSectionForComment"
                  @resetCommentPart="resetSectionForComment"
                  @saved="commentSaved"
                  @commentResolved="commentResolved"
              </div>
            </v-col>
          </v-row>
        </template>
        <v-row v-else-if="d.file_id">
          <v-col style>
            <div v-if="d.contentLoading" class="d-flex justify-center">
              <v-progress-circular indeterminate size="50" color="primary">
              </v-progress-circular>
            </div>
            <span v-else v-html="d.contentHtml"></span>
          </v-col>
        </v-row>
      </v-col>
    </v-row> -->
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
    <DocActionsMenu
      :context="docActionsMenuContext"
      @openDocument="openDocument"
      @copyDocument="$emit('copyDocument', $event)"
    ></DocActionsMenu>
  </div>
    </v-card-text>
  </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import ResponseHandler from "@/components/ResponseHandler"; // @ is an alias to /src
import DocActionsMenu from "@/components/common/DocActionsMenu";
import DocumentVersionComparePart from "@/components/audit/cDocumentVersionComparePart";
import DocumentVersionCompareTagSummary from "@/components/audit/cDocumentVersionCompareTagSummary";
import dayJS from "dayjs";
import utils from "@/common/utils.js";
import documentExport from "@/common/documentExport.js";
import nlpUtils from "@/common/nlpUtils.js";
import DocumentComments from "@/components/comments/CommentsContainer";
import ScrollTo from "vue-scrollto";
import { mapState } from "vuex";

export default {
  name: "DocumentVersionCompare",
  components: {
    ResponseHandler,
    DocActionsMenu,
    DocumentVersionComparePart,
    DocumentVersionCompareTagSummary,
    DocumentComments,
  },
  props: {
    compareList: { type: Array, required: true },
    show: { type: Boolean, default: false },
    multiDoc: { type: Boolean, default: false },
    showWorkflowActions: { type: Boolean, default: false },
    fixPrimaryDoc: { type: Boolean, default: false },
    hierarchySuggestions: { type: Object },
    title: { type: String, default: "Document Comparison" },
  },
  data: function () {
    return {
      documents: [],
      showInternal: false,
      isLoading: false,
      highlightChanges: false,
      isLoadingSuggestions: false,
      response: null,
      utils: utils,
      nlpUtils: nlpUtils,
      docActionsMenuContext: {
        show: false,
        busy: false,
        document: null,
        posX: 0,
        posY: 0,
      },
      containerHeight: "100%",
      draggedIndex: null,
      dragOverIndex: null,
      resizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
      viewSetting: { defaultColumnWidth: 600 },
      partCommentUpdate: null,
    };
  },
  updated() {
    this.$nextTick(() => {
      this.syncRowHeights();
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.setupHorizontalScrollSync();
    });
    // Add global mouse event listeners for resizing
    document.addEventListener('mousemove', this.handleResize);
    document.addEventListener('mouseup', this.stopResize);
  },
  beforeDestroy() {
    this.cleanup();
    this.$emit("close");
  },
  computed: {
    ...mapState({
      config: (state) => state.settings.config,
    }),
    currentDocId() {
      return this.compareList.find((d) => d.isCurrentDoc)?.doc_id;
    },
    showAllComments(){
      return this.config.settings.some(
          (s) => s.setting === 'document_show_all_comments' && s.value === "true"
      );
    },   
  },
  watch: {
    show(newval) {
      if (newval) {
        this.showInternal = true;        
        this.getData();
      }
    }
  },
  methods: {
    setContainerHeight() {
      this.setupScrollSync();
      let container = this.$refs.docContainer;
      if (container && container.scrollHeight > 0) {
        this.containerHeight = `${container.scrollHeight}px`;
      } else {
        this.containerHeight = "100%";
      }
    },
    async getData() {
      const params = {
        compareDocs: this.compareList
          .filter((d) => d.doc_id && !d.file_id)
          .map((d) => {
            return {
              doc_id: d.doc_id,
              activity_id: d.activity_id,
              viewAction: d.viewAction,
            }
          }),
      };
      
      const noViewActionDocs = params.compareDocs.filter(x => !x.viewAction);
      if (noViewActionDocs.length) {
        const docIds = [...new Set(noViewActionDocs.map(d => d.doc_id))];
        await this.$store.getters["docs/getUserViewActions"](docIds)
          .then(vas => vas.forEach(va => {
              params.compareDocs
                .filter(d => d.doc_id == va.doc_id)
                .forEach(d => d.viewAction = va.viewAction);
          }));
      }

      if (params.compareDocs.length === 0) {
        this.documents = this.compareList
          .filter((d) => d.file_id)
          .map((d) => {
            const file = {
              ...d,
              contentHtml: null,
              contentLoading: false,
            };
            this.fetchFileData(file);
            return file;
          });
        return;
      }

      this.isLoading = true;
      let noPerm = params.compareDocs.filter((x) => !x.viewAction).length;
      params.compareDocs = params.compareDocs.filter((x) => x.viewAction);
      let shoWarning = false;
      if (!params.compareDocs.length) {
        this.response = {
          Status: "warning",
          message: `You are not permitted to view any of the selected documents.`,
        };
        this.$emit("nodata");
        this.isLoading = false;
        return;
      } else if (noPerm) {
        shoWarning = true;
      }
      await this.getViewSetting();
      axios
        .post("document/documentVersions", params)
        .then((resp) => {
          if (resp.data.Status === "OK") {
            let documents = resp.data.Data;
            documents.forEach((d) => {
              d.created_date = dayJS(d.created_date).format("DD MMM YYYY");
              d.activity_date = dayJS(d.activity_date).format(
                "DD MMM YYYY HH:mm"
              );
              d.exporting = false;
              d.matchPctContent = this.getMatchPctContent(d);
              d.matchPctName = this.getMatchPctName(d);
              d.showComments = false;
              d.commentStyle = null;
              d.docPartStyle = null;
              d.docContainerStyle = null;
              d.docHeaderStyle = null;
              d.hover = false;
              d.comment_doc_part = null;
              d.highlightedDocPartForCommentHover = null;
              d.highlightedDocPartForCommentFocus = null;
            });
            let dat = [
              ...documents,
              ...this.compareList
                .filter((d) => d.file_id)
                .map((d) => {
                  const file = {
                    ...d,
                    contentHtml: null,
                    contentLoading: false,
                    hover: false,
                    docPartStyle: null,
                  };
                  this.fetchFileData(file);
                  return file;
                }),
            ];
            this.documents = dat;
            this.deriveColumnWidths();
          } else {
            alert(resp.data.Message);
          }
          if (shoWarning) {
            this.response = {
              Status: "warning",
              message: `NOTE: You are not permitted to view ${noPerm} of the selected documents.`,
            };
          } else {
            this.response = resp.data;
          }
          this.isLoading = false;
          setTimeout(() => {
            this.setContainerHeight();
            this.setupHorizontalScrollSync();
          }, 100);
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.log(err);
            this.response = err.response
              ? err.response.data
              : { message: "Unexpected Error" };
          }
          this.isLoading = false;
        });
    },
    fetchFileData(file) {
      file.contentLoading = true;
      nlpUtils.getDocText(file.file_id, file.row_id).then((response) => {
        if (
          response.status == 200 &&
          (response.data.rawText || response.data.rawData)
        ) {
          let sourceText =
            response.data.rawText?.match(/<[a-z][\s\S]*>/i)?.length > 0
              ? response.data.rawText
              : response.data.rawData;
          file.contentHtml = sourceText
            ?.replaceAll("<em>", "")
            ?.replaceAll("</em>", "");
        } else {
          this.response = response;
        }
        file.contentLoading = false;
        setTimeout(() => {
          this.setContainerHeight();
        }, 100);
      });
    },
    openDocActionsMenuContext(event, document) {
      event.preventDefault();
      let doc = {
        doc_id: document.doc_id,
        reference: document.reference,
        doc_name: document.doc_name,
        doc_type: document.doc_type,
      };
      this.docActionsMenuContext.document = doc;
      this.docActionsMenuContext.posX = event.clientX;
      this.docActionsMenuContext.posY = event.clientY;
      this.docActionsMenuContext.show = false;
      this.$nextTick(() => {
        this.docActionsMenuContext.show = true;
      });
    },
    openItem(item) {
      if (item.reference) this.openDocument(item);
      else if (item.file_id) this.nlpUtils.downloadFile(item.file_id);
    },
    openDocument(item) {
      if (!item.reference) return;

      this.$emit("openDocument", {
        system_number: utils.removeTags(item.reference),
        doc_name: utils.removeTags(item.doc_name),
        doc_type: item.doc_type,
        viewAction: item.viewAction,
      });
    },
    exportVersion(item, index, format) {
      this.exporting = true;
      const version = item.versions[index];
      version.exporting = true;
      const params = {
        compareDocs: [
          {
            doc_id: item.doc_id,
            activity_id: version.compare_activity_id,
            viewAction: this.$store.getters["docs/docsList"].find(
              (x) => x.doc_id === item.doc_id
            )?.viewAction,
          },
          {
            doc_id: item.doc_id,
            activity_id: version.document_activity_id,
            viewAction: this.$store.getters["docs/docsList"].find(
              (x) => x.doc_id === item.doc_id
            )?.viewAction,
          },
        ],
        format: format,
        highlightChanges: this.highlightChanges,
      };

      axios
        .get("document/documentVersionsExport", {
          params: params,
          responseType: "arraybuffer",
        })
        .then((resp) => {
          const docName = `${item.reference}_${version.status}Version_${dayJS(
            version.activity_date
          ).format("YYYYMMDDHHmm")}.${format}`;
          utils.downloadFile(
            resp.data,
            docName,
            documentExport.getMimeType(format)
          );
          version.exporting = false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.log(err);
            this.response = { message: "Unexpected Error" };
          }
          version.exporting = false;
        });
    },
    getMatchPctContent(d) {
      let pct = this.compareList.find(
        (x) => x.doc_id === d.doc_id
      )?.match_pct_content;
      if (pct) return Number.parseFloat(pct).toFixed();
      else return false;
    },
    getMatchPctName(d) {
      let pct = this.compareList.find(
        (x) => x.doc_id === d.doc_id
      )?.match_pct_name;
      if (pct) return Number.parseFloat(pct).toFixed();
      else return false;
    },
    syncRowHeights() {
      // Sync heights between corresponding rows
      if (this.documents.filter(d => !d.file_id).length < 2) return;
      let rows = [];
      this.documents.filter(d => !d.file_id).forEach((d, di) => {
        d.templateParts.forEach((tp, tpi) => {
          const partCount = tp.def.partsCount;
          for (let i = 1; i <= partCount; i++) {
            let fullRow = rows.find(
              (x) =>
                x.tpi === tpi && x.dpi === i && x.compareSet === d.compareSet
            );
            if (!fullRow) {
              fullRow = {
                tpi: tpi,
                dpi: i,
                compareSet: d.compareSet,
                elements: [],
              };
              rows.push(fullRow);
            }
            const rowElems =
              this.$refs[`set_${d.compareSet}_doc_${di}_row_${tpi}_${i}`];
            const elem = rowElems ? rowElems[0] : null;
            if (elem) {
              elem.style.minHeight = "auto";
              void elem.offsetHeight;
              fullRow.elements.push(elem);
            }
          }
        });
      });
      rows
        .filter((r) => r.elements.length)
        .forEach((r) => {
          const maxHeight = r.elements.reduce(
            (p, c) => (c.offsetHeight > p ? c.offsetHeight : p),
            0
          );
          r.elements.forEach((e) => (e.style.minHeight = `${maxHeight}px`));
        });
    },
    setupScrollSync() {
      if (this.documents.length < 2) return;
      let isScrolling = false;
      let scrollContainers = this.documents
        .map((d, di) =>
          this.$refs[`scrollContainer${di}`]?.length
            ? this.$refs[`scrollContainer${di}`][0]
            : null
        )
        .filter((x) => x);
      scrollContainers.forEach((d, di) => {
        this[`_scrollHandler${di}`] = () => {
          if (isScrolling) return;
          isScrolling = true;
          scrollContainers.forEach((sc) => (sc.scrollTop = d.scrollTop));
          requestAnimationFrame(() => {
            isScrolling = false;
          });
        };
        d.addEventListener("scroll", this[`_scrollHandler${di}`]);
      });
    },
    setupHorizontalScrollSync() {
      const headersWrapper = this.$refs.headersWrapper;
      const gridsWrapper = this.$refs.gridsWrapper;

      if (!headersWrapper || !gridsWrapper) return;

      let isScrolling = false;

      this._headerScrollHandler = () => {
        if (isScrolling) return;
        isScrolling = true;
        gridsWrapper.scrollLeft = headersWrapper.scrollLeft;
        requestAnimationFrame(() => {
          isScrolling = false;
        });
      };

      this._gridScrollHandler = () => {
        if (isScrolling) return;
        isScrolling = true;
        headersWrapper.scrollLeft = gridsWrapper.scrollLeft;
        requestAnimationFrame(() => {
          isScrolling = false;
        });
      };

      headersWrapper.addEventListener("scroll", this._headerScrollHandler);
      gridsWrapper.addEventListener("scroll", this._gridScrollHandler);
    },
    cleanup() {
      let scrollContainers = this.documents
        .map((d, di) =>
          this.$refs[`scrollContainer${di}`]?.length
            ? this.$refs[`scrollContainer${di}`][0]
            : null
        )
        .filter((x) => x);
      scrollContainers.forEach((d, di) => {
        if (this[`_scrollHandler${di}`]) {
          d.removeEventListener("scroll", this[`_scrollHandler${di}`]);
        }
      });
      const headersWrapper = this.$refs.headersWrapper;
      const gridsWrapper = this.$refs.gridsWrapper;

      if (headersWrapper && this._headerScrollHandler) {
        headersWrapper.removeEventListener("scroll", this._headerScrollHandler);
      }
      if (gridsWrapper && this._gridScrollHandler) {
        gridsWrapper.removeEventListener("scroll", this._gridScrollHandler);
      }
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    showComments(doc, dp) {
      doc.showComments = dp ? true : !doc.showComments;
      this.deriveColumnWidths();
      this.setCommentDocPart(doc, dp);
    },
    setCommentDocPart(doc, part) {
      doc.comment_doc_part = part ? {        
        dp_id: part.dp_id,
        part_type: part.part_type,
        dpa_tag_id: part.dpa_tag_id || null,
        tag_value: part.tag_value || null,
        part_type_sequence: part.part_type_sequence
      } : null;
      this.highlightSectionForCommentFocus(doc, part);
    },
    findDocPart(doc, dp_id) {
      let docpart = null;
      const findPart = (data) => {
        if (!data?.length) return;
        let dp = data.find(x => x.dp_id === dp_id);
        if (!dp) {
          data.forEach(p => {
            if (!dp) dp = findPart(p.childParts);
          });
        }
        return dp;
      }
      doc.templateParts.forEach(tp => {
        if (!docpart) {
          docpart = findPart(tp.data);
        }
      });
      return docpart;
    },
    deriveColumnWidths() {
      let docDetailWidth = this.viewSetting.defaultColumnWidth;
      let commentWidth = 400;
      const availableWidth = window.innerWidth - 140;
      const availableHeight = window.innerHeight - (this.fixPrimaryDoc ? 322 : 320);
      this.maxHeight = `${availableHeight}px`;

      let usedWidth = this.documents.reduce(
        (p, c) => p + docDetailWidth + (c.showComments ? commentWidth : 0), 0);

      if (usedWidth < availableWidth) {
        docDetailWidth = (docDetailWidth * availableWidth) / usedWidth;
        commentWidth = (commentWidth * availableWidth) / usedWidth;
      }

      this.documents.forEach((d, di) => {
        let width = docDetailWidth;
        if (d.showComments) width += commentWidth;
        d.docContainerStyle = { minWidth: `${width}px`, maxWidth: `${width}px` };
        d.commentStyle = d.showComments ? { minWidth: `${commentWidth}px`, maxWidth: `${commentWidth}px` } : null;
        width = docDetailWidth - (di === 0 && this.fixPrimaryDoc ? 5 : 3); //allow for border
        if (d.file_id) width = docDetailWidth;
        d.docPartStyle = { minWidth: `${width}px`, maxWidth: `${width}px`, maxHeight: this.maxHeight };
        width = docDetailWidth - 2;
        d.docHeaderStyle = { minWidth: `${width - 2}px`, maxWidth: `${width - 2}px` };
      });
    },
    handleDragStart(event, index) {
      if (this.fixPrimaryDoc && index === 0) return;
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/html", event.target.innerHTML);
      // Add a subtle visual feedback
      event.target.style.opacity = "0.5";
    },
    handleDragEnd(event) {
      event.target.style.opacity = "1";
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    handleDragOver(event, index) {
      if (this.fixPrimaryDoc && index === 0) return;
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      this.dragOverIndex = index;
      event.dataTransfer.dropEffect = "move";
    },
    handleDragLeave() {
      this.dragOverIndex = null;
    },
    handleDrop(dropIndex) {
      if (this.fixPrimaryDoc && dropIndex === 0) return;
      if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
        this.dragOverIndex = null;
        return;
      }
      this.reOrder(this.draggedIndex, dropIndex);
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    removeDocument(index) {
      this.documents.splice(index, 1);
      this.deriveColumnWidths();
    },
    reOrder(oldIndex, newIndex) {
      if (this.fixPrimaryDoc && newIndex === 0) return;
      this.documents[oldIndex].hover = false;
      const newDocuments = [...this.documents];
      const movedItem = newDocuments[oldIndex];
      newDocuments.splice(oldIndex, 1);
      newDocuments.splice(newIndex, 0, movedItem);
      this.documents = newDocuments;

      this.$nextTick(() => {
        this.syncRowHeights();
      });
    },
    startResize(event) {
      event.preventDefault();
      this.resizing = true;
      this.resizeStartX = event.clientX;
      this.resizeStartWidth = this.viewSetting.defaultColumnWidth;
      // Add visual feedback
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    handleResize(event) {
      if (!this.resizing) return;

      event.preventDefault();
      const deltaX = event.clientX - this.resizeStartX;
      const newWidth = Math.max(400, this.resizeStartWidth + deltaX); // Minimum width of 400px
      this.viewSetting.defaultColumnWidth = newWidth;
      this.deriveColumnWidths();
      this.saveViewSetting();
    },
    stopResize() {
      if (!this.resizing) return;

      this.resizing = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Re-sync row heights after resize
      this.$nextTick(() => {
        this.syncRowHeights();
      });
    },
    async getViewSetting() {
      let resp = await this.$loginState.getUserViewSetting(this.$options._componentTag);
      if (resp) {
        this.viewSetting = { ...resp, ...this.viewSetting };
      }
    },
    async saveViewSetting() {
      let resp = await this.$loginState.saveUserViewSetting(this.$options._componentTag, this.viewSetting);
    },
    scrollToSectionForComment(comment, doc) {
      let options = {
        container: ".doc-part-container", //this.$refs.doccontainer, //"#container",
        easing: "ease-in",
        offset: -150,
        force: true,
        cancelable: true,
        x: false,
        y: true,
      };   
      ScrollTo.scrollTo("#container_" + comment.dp_id, 500, options);
      this.resetSectionForComment(doc);
    },
    commentSaved(data, doc) {
      let part = this.findDocPart(doc, data.dp_id);
      if (part) {
        part.comments_unresolved = 0;
        part.comments_total = 0;
        part.hasComments = 0;
        data.comments.forEach(c => {
          part.comments_total++;
          part.hasComments = 1;
          if (c.resolved === 0) part.comments_unresolved++;
        });
      }      
    },
    commentResolved(data, doc) {
      let part = this.findDocPart(doc, data.dp_id);
      if (part) {
        if (!data.document_comment_id) {
          part.comments_unresolved = data.resolved ? 0 : part.comments_total;
        } else {
          part.comments_unresolved += data.resolved ? -1 : 1;
        }
        if (part.comments_unresolved < 0) part.comments_unresolved = 0;
      }
    },
    resetSectionForComment(doc) {
      doc.highlightedDocPartForCommentFocus = null;
      doc.comment_doc_part = null;
    },
    highlightSectionForCommentHover(doc, part) {
      doc.highlightedDocPartForCommentHover = part;
    },  
    highlightSectionForCommentFocus(doc, part) {
      doc.highlightedDocPartForCommentFocus = part;
    },
  },
};
</script>
<style scoped lang="scss">
.doc-container {
  display: flex;
  overflow: auto;
  flex-wrap: nowrap;
  max-height: calc(90vh - 120px) !important;
  border-radius: 10px;
  &::-webkit-scrollbar-track {
    border-radius: 0 10px 10px 0;
  }
  .v-chip {
    border-radius: 4px;
    padding: 0 6px;
  }
  > .col {
    width: auto;
    max-width: unset;
    align-self: stretch;
    > .row {
      flex-wrap: nowrap;
      &.doc-header {
        position: sticky;
        top: 0;
        z-index: 2;
        > .col {
          padding: 15px;
          font-weight: bold;
          > .row > .col {
            min-height: 36px;
          }
        }
      }
      &.HeaderPart > .col,
      &.CentralHeaderPart > .col {
        padding: 25px 30px;
      }
      &.HeaderPart.tag > .col {
        padding: 15px 18px;
      }
      > .col {
        &.sticky {
          position: sticky;
          left: 0;
          z-index: 1;
        }
        &.first {
          padding-top: 30px;
        }
        min-width: 400px;
        width: auto;
        flex-grow: 1;
        padding: 20px 30px 15px 30px;

        @media only screen and (min-width: 960px) {
          min-width: calc((100vw - 101px) / 2);
        }

        @media only screen and (min-width: 1400px) {
          min-width: calc((100vw - 102px) / 3);
        }

        @media only screen and (min-width: 1900px) {
          min-width: calc((100vw - 103px) / 4);
        }

        @media only screen and (min-width: 2500px) {
          min-width: calc((100vw - 104px) / 5);
        }
      }
    }
  }
}

$background: #ffffff;
$headerBg: #eeeeee;
$border: #e5e5e5;
$oddRowBg: #f7f9fb;

.v-application.theme--light {
  .even {
    background-color: $oddRowBg;
  }
  .doc-container {
    border: 1px solid $border;
    .v-chip {
      background-color: #fff;
      border: 1px solid #cccccc;
    }
    > .col {
      &:not(:last-child) {
        border-right: 1px solid $border;
      }
      > .row {
        background-color: $background;
        &.doc-header > .col {
          background-color: $headerBg;
          border-bottom: 1px solid $border;
        }
        &.even > .col {
          background-color: $oddRowBg;
        }
        &.HeaderPart > .col {
          background-color: $headerBg;
          border-top: 1px solid $border;
          border-bottom: 1px solid $border;
        }
        > .col {
          background-color: $background;
          &:not(:last-child) {
            border-right: 1px solid $border;
          }
        }
      }
    }
    &::-webkit-scrollbar-track {
      background-color: $headerBg;
    }
  }
}

$background: #1e1e1e;
$headerBg: #2e2e2e;
$border: #424242;
$oddRowBg: #222222;

.v-application.theme--dark {
  .even {
    background-color: $oddRowBg;
  }
  .doc-container {
    border: 1px solid $border;
    .v-chip {
      background-color: #222222;
      border: 1px solid #606060;
    }
    > .col {
      &:not(:last-child) {
        border-right: 1px solid $border;
      }
      > .row {
        background-color: $background;
        &.doc-header > .col {
          background-color: $headerBg;
          border-bottom: 1px solid $border;
        }
        &.even > .col {
          background-color: $oddRowBg;
        }
        &.HeaderPart > .col {
          background-color: $headerBg;
          border-top: 1px solid $border;
          border-bottom: 1px solid $border;
        }
        > .col {
          background-color: $background;
          &:not(:last-child) {
            border-right: 1px solid $border;
          }
        }
      }
    }
    &::-webkit-scrollbar-track {
      background-color: $headerBg;
    }
  }
}
.HeaderPart {
  background-color: #eeeeee;
  // padding: 25px 30px;
}

.sync-grid-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  // border: 1px solid #e0e0e0;
  // border-radius: 8px;
  overflow: hidden;
}

/* Fixed Header */
.grid-headers-wrapper {
  position: sticky;
  top: 0;
  z-index: 10;
  overflow-x: auto;
  overflow-y: hidden;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.grid-headers {
  display: flex;
  align-items: stretch;
  padding-right: 10px;
}

.grid-wrappers {
  display: flex;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
}

.header-cell, .header-cell-fixed {
  // padding: 16px;
  font-weight: 700;
  font-size: 14px;
  font-family: "Martel Sans", sans-serif;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  position: relative;
}

.header-cell-fixed {
  cursor: not-allowed;
  border: 2px solid #50B3CB;
  border-bottom: 1px solid #e0e0e0;
}

.header-cell {
  cursor: move;
  transition: all 0.2s ease;

  &:hover {
    background-color: #eeeeee;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.dragging {
    opacity: 0.5;
    border: 2px dashed #4caf50;
  }

  &.drag-over {
    border: 2px solid #4caf50;
    background-color: #e8f5e9;
    transform: scale(1.02);
  }
}

.doc-detail-container, .doc-detail-container-fixed {
  display: flex;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.doc-detail-container {
  border: 1px solid #e0e0e0;
  border-top: none;
}
.doc-detail-container-fixed {
  border: 2px solid #50B3CB;
  border-top: none;
}
.doc-comment-container {
  border-left: 1px solid #e0e0e0;
}

/* Scrollable Content Container */
.doc-part-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  // max-height: 500px; /* Adjust as needed */
}

.grid-content {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Individual Grids */
.grid {
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ddd;
}

.grid:last-child {
  border-right: none;
}

/* Row Items */

.part-content {
  padding: 12px 16px;
  width: 100%;
  word-wrap: break-word;
}

/* Scrollbar styling (optional) */
.doc-part-container::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

.doc-part-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.doc-part-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: px;
}

.doc-part-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.grid-move-handle {
  height: 25px;
  display: flex;
  width: 125px;
  margin-left: calc(100% - 140px);
  background-color: #eeeeee;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-top: solid 1px #e0e0e0;
  border-left: solid 1px #e0e0e0;
  border-right: solid 1px #e0e0e0;
  // opacity: 0.3;
  transition: opacity 0.2s ease;

  .drag-handle {
    cursor: move;
  }

  &:hover {
    opacity: 0.7;
  }
}
.current-doc-label {
  border: 1px solid #50B3CB;
  background-color: #EEF7F9;
  height: 24px;
  border-radius: 4px;
  padding: 6px 8px;
  text-align: center;
  font-family: "DM Sans";
  font-weight: 700;
  font-size: 11px;
  line-height: 100%;
  letter-spacing: 0;
}
.header-comment, .header-doc {
  padding: 16px;
}
.header-comment {
  border-left: 1px solid #e0e0e0;
}

.resize-filler, .resize-handle {
  width: 10px;
  position: relative;
  flex-shrink: 0;
  display: flex;
}
.resize-handle {
  cursor: col-resize;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);

    .resize-handle-inner {
      background-color: #4CAF50;
      opacity: 1;
    }
  }

  &:active {
    background-color: rgba(76, 175, 80, 0.1);
  }
}

.resize-handle-inner {
  width: 3px;
  height: 40px;
  background-color: #ddd;
  border-radius: 2px;
  opacity: 0.5;
  transition: all 0.2s ease;
  pointer-events: none;
}

/* When resizing is active */
body.resizing {
  cursor: col-resize !important;
  user-select: none !important;
}

.highlightedCommentPartFocus {
  background-color: #d5e8fc;
}

.highlightedCommentPartHover {
  background-color: #e6f1fb;
}
</style>

<!--
  Non-scoped: `.v-dialog__content` teleports to <body>, outside this
  component's scoped subtree, so a `scoped` rule wouldn't reach it.

  The Atlas flyout sits at z-index 2400 so it can sit above ordinary
  Vuetify dialogs in the rest of the app. This comparison view is the
  exception — Atlas is what opened it, and the user wants to see the
  full comparison rather than the flyout overlapping it. Lifting the
  dialog above 2400 puts the comparison fully in front; the flyout
  remains visible and interactive on the right edge wherever the
  dialog's natural width doesn't reach.
-->
<style>
/*
  Two selectors because Vuetify's `content-class` lands on the inner
  `.v-dialog` box in this version, not the `.v-dialog__content` overlay
  that actually owns the stacking context. `:has()` walks up from the
  tagged child to its `.v-dialog__content` parent; the direct match is
  kept as a fallback in case Vuetify behaviour changes back.
*/
.v-dialog__content.dc-above-atlas-flyout,
.v-dialog__content:has(.dc-above-atlas-flyout) {
  z-index: 2500 !important;
}
</style>
